/**
 * Fonts for the PDF editor — the same catalogue the print editor offers.
 *
 * The whole Google Fonts library (~1,900 families) plus the local system faces
 * live in `print-template-editor/fonts`: a generated catalogue, on-demand
 * loading and aliased previews for the picker. Duplicating any of that here
 * would mean two lists drifting apart, so this module is a thin adapter that
 * speaks the PDF editor's vocabulary (a text object with `bold`/`italic`)
 * instead of the print editor's (weight numbers).
 */
import {
    FONT_CATALOG,
    FONT_CATEGORIES,
    FONT_SCRIPTS,
    ensureFontFamily,
    ensureFontPreview,
    getFamily,
    previewFamily,
    resolveVariant,
    whenFontReady,
} from '../../print-template-editor/fonts';
import { DEFAULT_FONT_FAMILY, OBJ } from '../constants';

export {
    FONT_CATALOG,
    FONT_CATEGORIES,
    FONT_SCRIPTS,
    ensureFontPreview,
    getFamily,
    previewFamily,
};

/** Whether a name is a family the catalogue knows (Google or system). */
export const isKnownFamily = (name) => !!getFamily(name);

/**
 * The cut to load for a box.
 *
 * The editor has one weight above regular and a slant, but a family may ship
 * neither — `resolveVariant` snaps to something it really has, so the browser
 * is never asked to fake a face the export would then draw differently.
 */
const cutFor = (family, bold, italic) => resolveVariant(family, bold ? 700 : 400, !!italic);

/**
 * The text a font has to be able to draw, as a probe for the loader.
 *
 * A family is split into files by character range, so asking for "the font" loads
 * only the file that covers a space. The probe is the characters actually on the
 * page, plus a Latin sample so a family used for a Bangla heading still has the
 * letters of a figure caption — and with no text at all, a sample of both.
 */
const probeFor = (text) => {
    const chars = [...new Set(String(text || ''))].filter((c) => c.trim()).join('');
    return `Hg${chars || 'Hello World'}`.slice(0, 600);
};

/** Load one family (in one cut) and wait until it can be measured with. */
export function loadFontFamily(family, bold = false, italic = false, text = '') {
    const name = family || DEFAULT_FONT_FAMILY;
    const cut = cutFor(name, bold, italic);
    ensureFontFamily(name, [cut]);
    return whenFontReady(name, cut.weight, cut.italic, 32, probeFor(text));
}

/**
 * Load every family a set of text styles needs.
 *
 * Anything that measures or draws must await this first: `document.fonts.load`
 * only resolves faces the page already declares, so a family nobody has fetched
 * yet measures as a fallback — and a paragraph laid out against the wrong
 * metrics wraps in the wrong places for the rest of its life.
 *
 * A style may carry the `text` it will draw; styles that share a family and cut
 * have their text merged, so each cut is probed once with everything it must set.
 *
 * @param {Array<{ fontFamily?: string, bold?: boolean, italic?: boolean, text?: string }>} styles
 */
export async function ensureFontsFor(styles = []) {
    const jobs = new Map();
    for (const style of styles) {
        const family = style?.fontFamily || DEFAULT_FONT_FAMILY;
        const key = `${family}|${style?.bold ? 1 : 0}|${style?.italic ? 1 : 0}`;
        const job = jobs.get(key) || { family, bold: style?.bold, italic: style?.italic, text: '' };
        job.text += style?.text || '';
        jobs.set(key, job);
    }
    await Promise.allSettled(
        [...jobs.values()].map((j) => loadFontFamily(j.family, j.bold, j.italic, j.text)),
    );
}

/**
 * The text-bearing objects whose families have to be loaded before a paint —
 * the box's own style, and the family of every styled run inside it.
 */
export const textStylesOf = (objects = []) => objects
    .filter((o) => o?.type === OBJ.TEXT || o?.type === OBJ.NOTE)
    .flatMap((o) => {
        const text = String(o.text || '');
        const styles = [{ fontFamily: o.fontFamily, bold: o.bold, italic: o.italic, text }];
        for (const m of o.marks || []) {
            const family = m.style?.fontFamily;
            if (!family && m.style?.bold === undefined && m.style?.italic === undefined) continue;
            styles.push({
                fontFamily: family || o.fontFamily,
                bold: m.style?.bold ?? o.bold,
                italic: m.style?.italic ?? o.italic,
                text: text.slice(m.start, m.end),
            });
        }
        return styles;
    });
